import { IVehicle } from "../models/Vehicle";
import PriceFormat from "./priceFormat";

/**
 * Vehicle-level dismantle recommendation (0-100) and verdict helpers.
 *
 * Mirrors the shape of `partPriority.ts` (which scores parts categories)
 * so the popover surfaces a familiar component-by-component breakdown.
 *
 *   Vehicle commonality:              0 – 15
 *   Engine / transmission:            0 – 15
 *   Sell-through:                     0 – 15
 *   Historical revenue:               0 – 10
 *   Adjusted parts value:             0 – 15
 *   Export score:                     0 – 10
 *   Scrap floor strength:             0 – 10
 *   Overstock penalty:              -30 –  0
 *   Risk penalty (unknown vehicle): -10 –  0
 *   ──────────────────────────────────────────
 *   Total (clamped 0-100)
 *
 * Verdict bands (clamped 0-100) — drives the Wilma Intelligence pill on
 * the Awaiting Inventory page so operators can decide at a glance whether
 * to send a vehicle for dismantling, salvage, or pause for review.
 *
 *   60-100 → Dismantle
 *   35-59  → Review
 *    0-34  → Skip
 */
export type DismantleVerdictId = "skip" | "review" | "dismantle";

export interface DismantleVerdictMeta {
  id: DismantleVerdictId;
  /** Long form, e.g. "Don't dismantle — salvage". */
  label: string;
  /** One-word form for compact chips. */
  shortLabel: string;
  description: string;
  min: number;
  max: number;
  /** 1 = top tier. */
  rank: number;
  /** Tailwind classes for the verdict-coloured chips and bars. */
  chipClass: string;
  ringClass: string;
  bgClass: string;
  textClass: string;
  barClass: string;
  segmentClass: string;
}

/** Ordered top-down — `DISMANTLE_VERDICTS[0]` is the strongest recommendation. */
export const DISMANTLE_VERDICTS: DismantleVerdictMeta[] = [
  {
    id: "dismantle",
    label: "Dismantle",
    shortLabel: "Dismantle",
    description:
      "Strong donor — parts demand, drivetrain value and sell-through all support dismantling.",
    min: 60,
    max: 100,
    rank: 1,
    chipClass: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    ringClass: "ring-emerald-200",
    bgClass: "bg-emerald-50",
    textClass: "text-emerald-700",
    barClass: "bg-emerald-500",
    segmentClass: "bg-emerald-500",
  },
  {
    id: "review",
    label: "Review",
    shortLabel: "Review",
    description:
      "Mixed signals — review parts mix, in-stock cover and salvage offer before committing.",
    min: 35,
    max: 59,
    rank: 2,
    chipClass: "bg-amber-50 text-amber-800 ring-amber-200",
    ringClass: "ring-amber-200",
    bgClass: "bg-amber-50",
    textClass: "text-amber-700",
    barClass: "bg-amber-500",
    segmentClass: "bg-amber-400",
  },
  {
    id: "skip",
    label: "Don't dismantle",
    shortLabel: "Skip",
    description:
      "Weak donor — parts demand or value insufficient versus salvage/scrap return.",
    min: 0,
    max: 34,
    rank: 3,
    chipClass: "bg-rose-50 text-rose-700 ring-rose-200",
    ringClass: "ring-rose-200",
    bgClass: "bg-rose-50",
    textClass: "text-rose-700",
    barClass: "bg-rose-500",
    segmentClass: "bg-rose-400",
  },
];

const VERDICTS_BY_ID: Record<DismantleVerdictId, DismantleVerdictMeta> =
  DISMANTLE_VERDICTS.reduce(
    (acc, verdict) => {
      acc[verdict.id] = verdict;
      return acc;
    },
    {} as Record<DismantleVerdictId, DismantleVerdictMeta>
  );

/** Returns the verdict metadata that contains the given clamped score. */
export function getDismantleVerdict(score: number): DismantleVerdictMeta {
  const clamped = Math.max(0, Math.min(100, Math.round(score)));
  for (const verdict of DISMANTLE_VERDICTS) {
    if (clamped >= verdict.min && clamped <= verdict.max) return verdict;
  }
  return VERDICTS_BY_ID.skip;
}

/* ─────────────────────────────────────────────────────────────────────
 * Score breakdown — the nine factors that compose the dismantle total.
 * Each component reports both:
 *   - `score`: the signed contribution to the total (positive or, for
 *              penalties, negative).
 *   - `detail`: a human-friendly string describing the underlying signal
 *               (e.g. "£1,250 drivetrain", "£420 already in stock").
 *
 * The hover popover on the Wilma Intelligence pill renders these so an
 * operator can see exactly why a vehicle scored where it did.
 * ───────────────────────────────────────────────────────────────────── */
export type DismantleBreakdownKey =
  | "vehicleCommonality"
  | "engineTransmission"
  | "sellThrough"
  | "historicalRevenue"
  | "adjustedPartsValue"
  | "exportScore"
  | "scrapFloor"
  | "overstockPenalty"
  | "riskPenalty";

export interface DismantleBreakdownComponent {
  /** Signed contribution to the total (penalties are negative). */
  score: number;
  /** Human-readable explanation of the underlying signal. */
  detail: string;
}

export type DismantleBreakdown = Record<
  DismantleBreakdownKey,
  DismantleBreakdownComponent
>;

export interface DismantleComponentMeta {
  key: DismantleBreakdownKey;
  label: string;
  description: string;
  min: number;
  max: number;
}

/** Ordered top-down — same order we use in the popover. */
export const DISMANTLE_COMPONENTS: DismantleComponentMeta[] = [
  {
    key: "vehicleCommonality",
    label: "Vehicle commonality",
    description:
      "How widely this make is dismantled in the UK — drives baseline parts demand.",
    min: 0,
    max: 15,
  },
  {
    key: "engineTransmission",
    label: "Engine / transmission",
    description:
      "Combined drivetrain value — engines and gearboxes are the biggest single-ticket sales.",
    min: 0,
    max: 15,
  },
  {
    key: "sellThrough",
    label: "Sell-through",
    description:
      "Projected sales as a share of theoretical parts value — closer to 1.0 means less wastage.",
    min: 0,
    max: 15,
  },
  {
    key: "historicalRevenue",
    label: "Historical revenue",
    description:
      "Track record for similar donors based on make popularity and vehicle freshness.",
    min: 0,
    max: 10,
  },
  {
    key: "adjustedPartsValue",
    label: "Adjusted parts value",
    description:
      "Realistic financial size of the parts opportunity after sell-through.",
    min: 0,
    max: 15,
  },
  {
    key: "exportScore",
    label: "Export score",
    description:
      "Combined Engines Worldwide / VFE offer strength for engine + frontend export.",
    min: 0,
    max: 10,
  },
  {
    key: "scrapFloor",
    label: "Scrap floor strength",
    description:
      "Salvage / trade-poor valuation — the downside if dismantling underperforms.",
    min: 0,
    max: 10,
  },
  {
    key: "overstockPenalty",
    label: "Overstock penalty",
    description:
      "Reduces the score when we already hold a meaningful share of this vehicle's parts in stock.",
    min: -30,
    max: 0,
  },
  {
    key: "riskPenalty",
    label: "Risk penalty",
    description:
      "Reduces the score for unknown / partially-identified vehicles where signals can't be trusted.",
    min: -10,
    max: 0,
  },
];

/** Sums the component scores (signed) and clamps the result to 0-100. */
export function totalScoreFromBreakdown(breakdown: DismantleBreakdown): number {
  const raw = DISMANTLE_COMPONENTS.reduce(
    (sum, comp) => sum + (breakdown[comp.key]?.score ?? 0),
    0
  );
  return Math.max(0, Math.min(100, Math.round(raw)));
}

/** Raw signed sum (may exceed 0-100) — useful for debugging / tooltips. */
export function rawScoreFromBreakdown(breakdown: DismantleBreakdown): number {
  return DISMANTLE_COMPONENTS.reduce(
    (sum, comp) => sum + (breakdown[comp.key]?.score ?? 0),
    0
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * Score derivation
 * ───────────────────────────────────────────────────────────────────── */

export interface DismantleRecommendationInputs {
  totalValueAllParts: number | null;
  projectedSales: number | null;
  valuePartsInStock: number | null;
  totalPartsInStock: number | null;
  engineValue: number | null;
  gearboxValue: number | null;
  exportValue: number | null;
  valuationTradePoor: number | null;
}

export interface IDismantleRecommendation {
  /** Clamped 0-100 score used for display / verdict resolution. */
  total: number;
  /** Raw signed sum before clamping (useful for diagnostics). */
  raw: number;
  /** The verdict band the clamped total falls into. */
  verdict: DismantleVerdictMeta;
  /** Per-component contributions. */
  breakdown: DismantleBreakdown;
}

// UK-market parts demand by make. Populated from the same logic that
// drives the mock financials so the commonality score and the underlying
// financials stay roughly aligned. Higher = stronger UK parts demand.
const COMMONALITY_TIERS: Record<string, number> = {
  // Mainstream UK volume — deepest parts demand.
  ford: 15,
  vauxhall: 14,
  opel: 13,
  volkswagen: 14,
  bmw: 14,
  audi: 14,
  "mercedes-benz": 13,
  mercedes: 13,
  toyota: 12,
  nissan: 12,
  peugeot: 12,
  renault: 11,
  citroen: 11,
  hyundai: 11,
  kia: 11,
  skoda: 12,
  seat: 11,
  mini: 12,
  honda: 11,
  mazda: 11,
  // Mid-tier — solid demand, narrower buyer pool.
  volvo: 10,
  jaguar: 9,
  "land rover": 11,
  "range rover": 11,
  fiat: 9,
  dacia: 8,
  suzuki: 8,
  lexus: 9,
  smart: 7,
  chevrolet: 7,
  // Premium / rare — niche demand, low search velocity.
  porsche: 7,
  tesla: 7,
  "aston martin": 5,
  bentley: 5,
  maserati: 5,
  ferrari: 4,
  lamborghini: 4,
  "rolls-royce": 4,
};

const commonalityDescriptor = (score: number): string => {
  if (score >= 13) return "very common — strong parts demand";
  if (score >= 10) return "common — healthy parts demand";
  if (score >= 7) return "mid-tier — moderate demand";
  if (score > 0) return "niche — limited buyer pool";
  return "unknown — no make detected";
};

const lookupCommonality = (make: string | null | undefined): number | null => {
  if (!make) return null;
  const key = make.toLowerCase().trim();
  if (COMMONALITY_TIERS[key] !== undefined) return COMMONALITY_TIERS[key];
  // Fall back to first token so e.g. "BMW M3" or "Land Rover Defender" match.
  const firstWord = key.split(/\s+/)[0];
  if (COMMONALITY_TIERS[firstWord] !== undefined)
    return COMMONALITY_TIERS[firstWord];
  return null;
};

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

// Awards `score` based on which `[threshold, score]` band `value` falls
// into. Bands are sorted descending by threshold (largest first).
const bandedScore = (
  value: number,
  bands: Array<[threshold: number, score: number]>,
  fallback: number
): number => {
  for (const [threshold, score] of bands) {
    if (value >= threshold) return score;
  }
  return fallback;
};

const fmtMoney = (v: number | null) =>
  v === null || v === undefined ? "—" : PriceFormat(v);

const scoreVehicleCommonality = (
  vehicle: IVehicle
): DismantleBreakdownComponent => {
  const make = vehicle.combinedMake ?? vehicle.dvlaMake ?? null;
  const direct = lookupCommonality(make);
  if (direct !== null) {
    return {
      score: direct,
      detail: `${make} — ${commonalityDescriptor(direct)}`,
    };
  }
  if (make) {
    return {
      score: 6,
      detail: `${make} — uncommon make, limited buyer pool`,
    };
  }
  return { score: 4, detail: "Make unknown — assumed below average" };
};

const scoreEngineTransmission = (
  engineValue: number | null,
  gearboxValue: number | null
): DismantleBreakdownComponent => {
  if (engineValue === null && gearboxValue === null) {
    return { score: 0, detail: "No drivetrain valuation" };
  }
  const total = (engineValue ?? 0) + (gearboxValue ?? 0);
  const score = bandedScore(
    total,
    [
      [1500, 15],
      [1100, 13],
      [800, 11],
      [600, 9],
      [400, 7],
      [250, 5],
      [100, 3],
      [1, 1],
    ],
    0
  );
  return { score, detail: `${PriceFormat(total)} drivetrain value` };
};

const scoreSellThrough = (
  totalValueAllParts: number | null,
  projectedSales: number | null
): DismantleBreakdownComponent => {
  if (
    !totalValueAllParts ||
    totalValueAllParts <= 0 ||
    projectedSales === null
  ) {
    return { score: 0, detail: "Sell-through unavailable" };
  }
  const ratio = projectedSales / totalValueAllParts;
  const pct = Math.round(ratio * 100);
  const score = bandedScore(
    ratio,
    [
      [0.7, 15],
      [0.6, 12],
      [0.5, 9],
      [0.4, 6],
      [0.3, 3],
      [0.15, 1],
    ],
    0
  );
  return { score, detail: `${pct}% projected sales / theoretical value` };
};

const scoreHistoricalRevenue = (
  vehicle: IVehicle,
  commonalityScore: number
): DismantleBreakdownComponent => {
  // Proxy: track record for similar donors. We don't have per-vehicle
  // historic revenue, so we model it from make popularity (capped) and
  // freshness signals (year + mileage) — the same factors that correlate
  // with realised sales in the wider Wilma stock data.
  const commonalityComponent = clamp((commonalityScore / 15) * 6, 0, 6);

  const year = vehicle.dvlaYearOfManufacture ?? null;
  const currentYear = new Date().getFullYear();
  let youth = 0.5;
  if (year) {
    const age = currentYear - year;
    youth = clamp(1 - age / 22, 0, 1);
  }

  const miles = vehicle.odometerInMiles ?? null;
  let freshness = 0.5;
  if (miles !== null && miles !== undefined) {
    freshness = clamp(1 - (miles - 40000) / 200000, 0, 1);
  }

  const freshnessComponent = clamp((youth * 0.6 + freshness * 0.4) * 4, 0, 4);
  const score = Math.round(commonalityComponent + freshnessComponent);

  const yearText = year ? `${year}` : "year unknown";
  const milesText =
    miles !== null && miles !== undefined
      ? `${miles.toLocaleString("en-GB")} mi`
      : "mileage unknown";
  return {
    score: clamp(score, 0, 10),
    detail: `${yearText} · ${milesText}`,
  };
};

const scoreAdjustedPartsValue = (
  totalValueAllParts: number | null,
  projectedSales: number | null
): DismantleBreakdownComponent => {
  if (totalValueAllParts === null && projectedSales === null) {
    return { score: 0, detail: "Parts value unavailable" };
  }
  // Adjusted value = the more conservative of projected sales vs. 65% of
  // theoretical total. Avoids over-weighting vehicles with very generous
  // theoretical totals but weak realised sell-through.
  const conservativeProjected =
    projectedSales !== null ? projectedSales : (totalValueAllParts ?? 0) * 0.6;
  const conservativeTotal =
    totalValueAllParts !== null ? totalValueAllParts * 0.65 : Infinity;
  const adjusted = Math.min(conservativeProjected, conservativeTotal);

  const score = bandedScore(
    adjusted,
    [
      [4000, 15],
      [3000, 13],
      [2000, 11],
      [1500, 9],
      [1000, 7],
      [600, 5],
      [300, 3],
      [100, 1],
    ],
    0
  );
  return { score, detail: `${PriceFormat(adjusted)} realistic parts return` };
};

const scoreExport = (
  exportValue: number | null
): DismantleBreakdownComponent => {
  if (exportValue === null || exportValue <= 0) {
    return { score: 0, detail: "No export offer captured" };
  }
  const score = bandedScore(
    exportValue,
    [
      [1500, 10],
      [1000, 8],
      [600, 6],
      [350, 4],
      [150, 2],
      [1, 1],
    ],
    0
  );
  return { score, detail: `${PriceFormat(exportValue)} export offer` };
};

const scoreScrapFloor = (
  valuationTradePoor: number | null
): DismantleBreakdownComponent => {
  if (valuationTradePoor === null) {
    return { score: 0, detail: "No salvage valuation" };
  }
  const score = bandedScore(
    valuationTradePoor,
    [
      [900, 10],
      [600, 8],
      [400, 6],
      [250, 4],
      [120, 2],
      [1, 1],
    ],
    0
  );
  return {
    score,
    detail: `${PriceFormat(valuationTradePoor)} trade-poor valuation`,
  };
};

const scoreOverstockPenalty = (
  totalValueAllParts: number | null,
  valuePartsInStock: number | null
): DismantleBreakdownComponent => {
  if (
    !totalValueAllParts ||
    totalValueAllParts <= 0 ||
    valuePartsInStock === null
  ) {
    return { score: 0, detail: "No in-stock cover detected" };
  }
  const ratio = valuePartsInStock / totalValueAllParts;
  const pct = Math.round(ratio * 100);
  const penalty = bandedScore(
    ratio,
    [
      [0.5, -30],
      [0.35, -22],
      [0.2, -14],
      [0.1, -7],
      [0.04, -3],
    ],
    0
  );
  if (penalty === 0) {
    return {
      score: 0,
      detail: `${fmtMoney(valuePartsInStock)} already in stock (${pct}%)`,
    };
  }
  return {
    score: penalty,
    detail: `${pct}% of parts already in stock (${fmtMoney(valuePartsInStock)})`,
  };
};

const scoreRiskPenalty = (vehicle: IVehicle): DismantleBreakdownComponent => {
  const reasons: string[] = [];
  let penalty = 0;

  const make = vehicle.combinedMake ?? vehicle.dvlaMake ?? null;
  if (!make) {
    penalty -= 4;
    reasons.push("make missing");
  }

  if (!vehicle.dvlaYearOfManufacture) {
    penalty -= 3;
    reasons.push("year missing");
  }

  if (vehicle.odometerInMiles === null || vehicle.odometerInMiles === undefined) {
    penalty -= 2;
    reasons.push("mileage missing");
  }

  if (vehicle.found === false) {
    penalty -= 2;
    reasons.push("not yet matched on DVLA");
  }

  if (vehicle.scrappedRemovedMarker === true) {
    penalty -= 2;
    reasons.push("DVLA scrap marker set");
  }

  penalty = clamp(penalty, -10, 0);

  if (penalty === 0) {
    return { score: 0, detail: "No risk flags raised" };
  }
  return { score: penalty, detail: reasons.join(", ") };
};

/**
 * Compute the dismantle recommendation for a vehicle. The financial
 * `inputs` should already be resolved by the caller (real backend
 * analytics if available, mock fallback otherwise) so this function
 * stays a pure scoring routine.
 */
export function computeDismantleRecommendation(
  vehicle: IVehicle,
  inputs: DismantleRecommendationInputs
): IDismantleRecommendation {
  const commonality = scoreVehicleCommonality(vehicle);
  const engineTransmission = scoreEngineTransmission(
    inputs.engineValue,
    inputs.gearboxValue
  );
  const sellThrough = scoreSellThrough(
    inputs.totalValueAllParts,
    inputs.projectedSales
  );
  const historicalRevenue = scoreHistoricalRevenue(vehicle, commonality.score);
  const adjustedPartsValue = scoreAdjustedPartsValue(
    inputs.totalValueAllParts,
    inputs.projectedSales
  );
  const exportScore = scoreExport(inputs.exportValue);
  const scrapFloor = scoreScrapFloor(inputs.valuationTradePoor);
  const overstockPenalty = scoreOverstockPenalty(
    inputs.totalValueAllParts,
    inputs.valuePartsInStock
  );
  const riskPenalty = scoreRiskPenalty(vehicle);

  const breakdown: DismantleBreakdown = {
    vehicleCommonality: commonality,
    engineTransmission,
    sellThrough,
    historicalRevenue,
    adjustedPartsValue,
    exportScore,
    scrapFloor,
    overstockPenalty,
    riskPenalty,
  };

  const raw = rawScoreFromBreakdown(breakdown);
  const total = totalScoreFromBreakdown(breakdown);
  const verdict = getDismantleVerdict(total);

  return { total, raw, verdict, breakdown };
}
